import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { kinds } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { CatalogHead } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { contentTypesGet } from '@/shared/api/generated/content/content';
import { pluralize } from '@/shared/lib/plural';
import { ServerRulesEditor } from '@/features/rules/ServerRulesEditor';
import { WorkflowEditor } from '@/features/stages/WorkflowEditor';
import { Button } from '@/shared/ui/Button';
import { Counter } from '@/shared/ui/Counter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/Tabs';

import { RemoveTypeDialog } from '../SchemaPage/RemoveTypeDialog';
import { TypeFields } from '../SchemaPage/TypeFields';
import { TypeSettingsForm } from '../SchemaPage/TypeSettingsForm';
import { FormApi } from './FormApi';
import { Submissions } from './SubmissionsList';
import type { FormTab } from './tabs';
import { SUBMISSIONS } from './words';

/**
 * Форма: заявки, поля, подключение по API и настройки — вкладками, как у
 * модели. Вкладка — в адресе: «API» можно отдать разработчику ссылкой.
 */
export function FormPage() {
  const { siteId, env, typeId } = useParams({ from: '/authed/sites/$siteId/$env/forms/$typeId' });
  const { tab = 'submissions' } = useSearch({ from: '/authed/sites/$siteId/$env/forms/$typeId' });
  const navigate = useNavigate();
  const scope = useSiteScope();
  const [removing, setRemoving] = useState(false);
  const shaper = scope?.can('site.shape') ?? false;

  const details = useQuery({
    queryKey: ['content-type', typeId],
    queryFn: async () => {
      const response = await contentTypesGet(typeId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос формы');
      }

      return response.data;
    },
  });

  const type = details.data?.type;
  const fields = details.data?.fields ?? [];

  const setTab = (next: string) => {
    void navigate({
      to: '/sites/$siteId/$env/forms/$typeId',
      params: { siteId, env, typeId },
      search: next === 'submissions' ? {} : { tab: next as FormTab },
      replace: true,
    });
  };

  if (type === undefined) {
    return details.isError ? (
      <p className={catalog['empty']}>Такой формы нет — возможно, её удалили.</p>
    ) : null;
  }

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title={type.name}
        subtitle={
          <>
            <span className={catalog['mono']}>{type.slug}</span>
            {` · ${pluralize(type.records_count, SUBMISSIONS)}`}
            {type.api_access.create !== 'public' && ' · на паузе: сайт не может присылать заявки'}
            {type.description !== '' && ` · ${type.description}`}
          </>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="submissions" active={tab === 'submissions'}>
            Заявки <Counter value={type.unread_count} tone="accent" />
          </TabsTrigger>
          <TabsTrigger value="fields" active={tab === 'fields'}>
            Поля
          </TabsTrigger>
          <TabsTrigger value="stages" active={tab === 'stages'}>
            Статусы
          </TabsTrigger>
          <TabsTrigger value="rules" active={tab === 'rules'}>
            Правила
          </TabsTrigger>
          <TabsTrigger value="api" active={tab === 'api'}>
            API
          </TabsTrigger>
          {shaper && (
            <TabsTrigger value="settings" active={tab === 'settings'}>
              Настройки
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="submissions">
          <Submissions
            key={`${typeId}:${env}`}
            siteId={siteId}
            env={env}
            type={type}
            fields={fields}
            canDelete={scope?.can('content.write') ?? false}
          />
        </TabsContent>

        <TabsContent value="fields">
          <TypeFields type={type} readOnly={!shaper} />
        </TabsContent>

        <TabsContent value="stages">
          <WorkflowEditor key={type.etag} type={type} readOnly={!shaper} />
        </TabsContent>

        <TabsContent value="rules">
          <ServerRulesEditor key={type.etag} type={type} readOnly={!shaper} />
        </TabsContent>

        <TabsContent value="api">
          <FormApi siteId={siteId} env={env} type={type} fields={fields} />
        </TabsContent>

        {shaper && (
          <TabsContent value="settings">
            <div className={catalog['page']}>
              <TypeSettingsForm
                key={type.etag}
                siteID={siteId}
                environment={kinds[env]}
                type={type}
                section="form"
                onSaved={(saved) => {
                  void details.refetch();

                  // Перевели в «Модели» — туда и ведём.
                  if (saved.purpose !== 'form') {
                    void navigate({
                      to: '/sites/$siteId/$env/models/$typeId',
                      params: { siteId, env, typeId },
                      search: { tab: 'settings' },
                    });
                  }
                }}
              />
              <div>
                <Button
                  variant="ghost"
                  tone="danger"
                  iconLeft={<Trash2 size={15} />}
                  onClick={() => {
                    setRemoving(true);
                  }}
                >
                  Убрать форму в корзину
                </Button>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <RemoveTypeDialog
        type={removing ? type : undefined}
        onClose={() => {
          setRemoving(false);
        }}
        onRemoved={() => {
          void navigate({ to: '/sites/$siteId/$env/forms', params: { siteId, env } });
        }}
      />
    </div>
  );
}
